import { supabase, getSupabaseConfigError, formatSupabaseError } from '../../supabase';
import { isNetworkError, isOnline } from '../offline/network';
import { clearCachedAccount, getCachedAccount, saveCachedAccount } from '../offline/accountCache';
import { stampCreatedAt } from '../../utils/dbTimestamps';
import { setActiveGymId, resetGymIdCache } from '../receptionist/gymContext';
import { setCurrentReceptionistId } from '../receptionist/receptionistContext';
import {
  setAdminSignupEmail,
  loadAdminSettings,
  saveAdminSettings,
  ensureGymAdminSenderEmail,
} from '../../pages/admin/settings/settingsData';
import {
  getPriceForPlan,
  billingCycleFromDuration,
  computeExpiryDate,
} from '../owner/subscriptionsApi';

export const ROLE_ROUTES = {
  owner: '/owner/dashboard',
  admin: '/admin/dashboard',
  receptionist: '/receptionist/dashboard',
};

export const SUBSCRIPTION_EXPIRED_MESSAGE =
  'Your gym subscription has expired. Please renew your plan to continue using the admin panel.';

const TRIAL_DAYS = 7;

function mapPlanRow(row) {
  return {
    id: row.id,
    name: row.name,
    monthlyPrice: Number(row.monthly_price ?? row.monthlyPrice ?? 0),
    quarterlyPrice: Number(row.quarterly_price ?? row.quarterlyPrice ?? 0),
    yearlyPrice: Number(row.yearly_price ?? row.yearlyPrice ?? 0),
    lifetimePrice: Number(row.lifetime_price ?? row.lifetimePrice ?? 0),
    features: row.features || '',
    status: row.status || 'active',
    trialDays: Number(row.trial_days ?? row.trialDays ?? TRIAL_DAYS),
  };
}

export async function fetchSaasPlans() {
  const { data, error } = await supabase
    .from('saas_plans')
    .select('*')
    .eq('status', 'active')
    .order('sort_order', { ascending: true });

  if (error || !data?.length) return [];

  // Hide duplicate package names on signup (keep first by sort_order)
  const seen = new Set();
  return data.map(mapPlanRow).filter((plan) => {
    const key = String(plan.name || '').trim().toLowerCase();
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export async function fetchUserAccount(authUserId, email) {
  try {
    const { data, error } = await supabase
      .from('user_accounts')
      .select('*')
      .eq('auth_user_id', authUserId)
      .eq('status', 'active')
      .maybeSingle();

    if (error) {
      throw error;
    }

    let profile = null;
    if (data) {
      profile = await enrichAccountGymId(data, authUserId);
    } else if (email) {
      const { data: byEmail, error: emailError } = await supabase
        .from('user_accounts')
        .select('*')
        .ilike('email', email.trim())
        .eq('status', 'active')
        .maybeSingle();

      if (emailError) throw emailError;

      if (byEmail) {
        await supabase
          .from('user_accounts')
          .update({ auth_user_id: authUserId })
          .eq('id', byEmail.id);
        profile = await enrichAccountGymId({ ...byEmail, auth_user_id: authUserId }, authUserId);
      }
    }

    if (profile) {
      await saveCachedAccount(profile, { id: authUserId, email: profile.email || email });
    }
    return profile;
  } catch (error) {
    if (isNetworkError(error) || !isOnline()) {
      const cached = await getCachedAccount(authUserId);
      if (cached) return { ...cached, __offline: true };
    }
    throw new Error(formatSupabaseError(error, 'Could not load your account.'));
  }
}

async function enrichAccountGymId(account, authUserId) {
  if (!authUserId) return account;

  if (account.role === 'receptionist') {
    let receptionist = null;

    if (account.staff_id) {
      const { data } = await supabase
        .from('receptionists')
        .select('id, gym_id, branch_id, branch_name, email')
        .eq('id', account.staff_id)
        .maybeSingle();
      receptionist = data;
    }

    if (!receptionist) {
      const { data } = await supabase
        .from('receptionists')
        .select('id, gym_id, branch_id, branch_name, email')
        .eq('auth_user_id', authUserId)
        .maybeSingle();
      receptionist = data;
    }

    if (!receptionist && account.email) {
      const { data } = await supabase
        .from('receptionists')
        .select('id, gym_id, branch_id, branch_name, email')
        .ilike('email', account.email.trim())
        .maybeSingle();
      receptionist = data;
    }

    if (receptionist) {
      const gymId = account.gym_id || receptionist.gym_id;
      if (gymId && !account.gym_id) {
        await supabase.from('user_accounts').update({ gym_id: gymId }).eq('id', account.id);
      }
      return {
        ...account,
        gym_id: gymId,
        staff_id: account.staff_id || receptionist.id,
      };
    }
  }

  if (account.gym_id) return account;

  if (account.role === 'admin') {
    let admin = null;

    const { data: byAuth } = await supabase
      .from('admins')
      .select('id, gym_id, email')
      .eq('auth_user_id', authUserId)
      .maybeSingle();
    admin = byAuth;

    if (!admin?.gym_id && account.email) {
      const { data: byEmail } = await supabase
        .from('admins')
        .select('id, gym_id, email')
        .ilike('email', account.email.trim())
        .maybeSingle();
      admin = byEmail || admin;
      if (byEmail?.id && !byEmail.auth_user_id) {
        await supabase.from('admins').update({ auth_user_id: authUserId }).eq('id', byEmail.id);
      }
    }

    if (!admin?.gym_id && account.email) {
      const { data: panelUser } = await supabase
        .from('panel_users')
        .select('gym_id')
        .eq('panel', 'admin')
        .ilike('email', account.email.trim())
        .maybeSingle();
      if (panelUser?.gym_id) {
        admin = { gym_id: panelUser.gym_id };
      }
    }

    if (admin?.gym_id) {
      await supabase.from('user_accounts').update({ gym_id: admin.gym_id }).eq('id', account.id);
      return { ...account, gym_id: admin.gym_id };
    }
  }

  return account;
}

async function syncAdminGymProfile(gymId, email) {
  const { data: gym } = await supabase
    .from('gyms')
    .select('*')
    .eq('id', gymId)
    .maybeSingle();

  if (!gym) return;

  const settings = loadAdminSettings(gymId);
  const code = gym.currency_code || settings.currency.code;
  saveAdminSettings({
    ...settings,
    gym: {
      ...settings.gym,
      name: gym.name || settings.gym.name,
      tagline: gym.tagline || settings.gym.tagline,
      phone: gym.phone || settings.gym.phone,
      address: gym.address || settings.gym.address,
      website: gym.website || settings.gym.website,
      openingHours: gym.opening_hours || settings.gym.openingHours,
      timezone: gym.timezone || settings.gym.timezone,
    },
    currency: {
      ...settings.currency,
      code,
      symbol: gym.currency_symbol || settings.currency.symbol,
      position: gym.currency_position || settings.currency.position,
      decimalPlaces: Number(gym.decimal_places ?? settings.currency.decimalPlaces ?? 0),
    },
    logoPreview: gym.logo_url || settings.logoPreview,
  }, gymId);

  if (email || gym.contact_email) {
    setAdminSignupEmail(email || gym.contact_email, gymId);
  }
}

/** Latest gym subscription must be active / trial-not-ended for admin login */
const subscriptionCheckCache = new Map();
const SUBSCRIPTION_CHECK_TTL_MS = 5 * 60_000;

export async function validateAdminSubscription(gymId, { force = false } = {}) {
  if (!gymId) {
    return { valid: false, reason: SUBSCRIPTION_EXPIRED_MESSAGE };
  }

  const cached = subscriptionCheckCache.get(gymId);
  if (!force && cached && Date.now() < cached.expires) {
    return cached.result;
  }

  const { data, error } = await supabase
    .from('gym_subscriptions')
    .select('status, expiry_date, trial_ends_at')
    .eq('gym_id', gymId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    if (isNetworkError(error) || !isOnline()) {
      if (cached?.result) return cached.result;
      return { valid: true, reason: '', offline: true };
    }
    return { valid: false, reason: formatSupabaseError(error, SUBSCRIPTION_EXPIRED_MESSAGE) };
  }

  if (!data) {
    return { valid: false, reason: SUBSCRIPTION_EXPIRED_MESSAGE };
  }

  const now = new Date();
  const blockedStatuses = ['expired', 'cancelled', 'suspended'];
  if (blockedStatuses.includes(data.status)) {
    const result = { valid: false, reason: SUBSCRIPTION_EXPIRED_MESSAGE };
    subscriptionCheckCache.set(gymId, { result, expires: Date.now() + SUBSCRIPTION_CHECK_TTL_MS });
    return result;
  }

  if (data.status === 'trial' && data.trial_ends_at && new Date(data.trial_ends_at) < now) {
    const result = { valid: false, reason: 'Your 7-day free trial has ended. Please subscribe to continue.' };
    subscriptionCheckCache.set(gymId, { result, expires: Date.now() + SUBSCRIPTION_CHECK_TTL_MS });
    return result;
  }

  if (data.expiry_date) {
    const expiry = new Date(`${data.expiry_date}T23:59:59`);
    if (expiry < now) {
      const result = { valid: false, reason: SUBSCRIPTION_EXPIRED_MESSAGE };
      subscriptionCheckCache.set(gymId, { result, expires: Date.now() + SUBSCRIPTION_CHECK_TTL_MS });
      return result;
    }
  }

  const result = { valid: true };
  subscriptionCheckCache.set(gymId, { result, expires: Date.now() + SUBSCRIPTION_CHECK_TTL_MS });
  return result;
}

export async function assertAdminCanLogin(account) {
  if (account?.role !== 'admin') return;
  if (account.status && account.status !== 'active') {
    throw new Error('Your admin account is suspended. Contact the platform owner.');
  }

  const check = await validateAdminSubscription(account.gym_id);
  if (!check.valid) {
    await supabase.auth.signOut();
    clearSessionContext();
    throw new Error(check.reason || SUBSCRIPTION_EXPIRED_MESSAGE);
  }

  const { data: adminRow, error: adminLookupError } = await supabase
    .from('admins')
    .select('status')
    .eq('auth_user_id', account.auth_user_id)
    .maybeSingle();

  if (!adminLookupError && adminRow?.status && adminRow.status !== 'active') {
    await supabase.auth.signOut();
    clearSessionContext();
    throw new Error('Your admin account is suspended. Contact the platform owner.');
  }
}

export function applySessionContext(account) {
  if (!account) return;

  // Always reset first so a previous gym cannot leak into this session
  clearSessionContext();

  if (account.gym_id) {
    setActiveGymId(account.gym_id);
    if (account.role === 'admin') {
      syncAdminGymProfile(account.gym_id, account.email).catch(() => {});
      // Prefetch dashboard / members as soon as session is bound
      import('../panelBootstrap')
        .then(({ warmPanelData }) => warmPanelData(account.gym_id, { role: 'admin' }))
        .catch(() => {});
    }
  }

  if (account.role === 'receptionist' && account.staff_id) {
    setCurrentReceptionistId(account.staff_id);
  }

  if (account.role === 'receptionist') {
    // Branch sync only here — layout warms after branch is ready (avoids double warm / no-branch empty cache).
    import('../receptionist/receptionistApi')
      .then(({ syncReceptionistBranchFromProfile }) => syncReceptionistBranchFromProfile())
      .catch(() => {});
  }

  if (account.role === 'owner') {
    import('../panelBootstrap')
      .then(({ warmOwnerPanelData }) => warmOwnerPanelData())
      .catch(() => {});
  }

  if (account.role === 'admin' && account.email && account.gym_id) {
    ensureGymAdminSenderEmail(account.email, '', account.gym_id);
  }
}

export function clearSessionContext() {
  resetGymIdCache();
  setCurrentReceptionistId('');
  import('../gym/branchContext')
    .then(({ setReceptionistBranch }) => setReceptionistBranch(''))
    .catch(() => {});
}

async function explainLoginFailure(email) {
  const trimmed = email?.trim();
  if (!trimmed) {
    return 'Invalid email or password.';
  }

  const { data: receptionist } = await supabase
    .from('receptionists')
    .select('id, full_name, email, has_login, auth_user_id')
    .ilike('email', trimmed)
    .limit(1)
    .maybeSingle();

  if (receptionist && !receptionist.has_login && !receptionist.auth_user_id) {
    return 'Your receptionist profile exists, but login was never created (signup was blocked). Ask your gym admin to open Edit Receptionist, set a password, and save — or create the user in Supabase → Authentication → Users.';
  }

  const { data: account } = await supabase
    .from('user_accounts')
    .select('id, auth_user_id, role')
    .ilike('email', trimmed)
    .eq('role', 'receptionist')
    .maybeSingle();

  if (account && !account.auth_user_id) {
    return 'Receptionist account is not linked to Supabase Auth yet. Ask your admin to run supabase/add_receptionist_manual.sql in Supabase SQL Editor.';
  }

  if (receptionist?.has_login) {
    return 'Invalid email or password. Use the password from Supabase Authentication. If your admin created the user in the Supabase dashboard, use that password (not an old form password that failed to save).';
  }

  return 'Invalid email or password. Check spelling, or ask your gym admin to set up your login.';
}

export async function signIn(email, password, options = {}) {
  const configError = getSupabaseConfigError();
  if (configError) throw new Error(configError);

  const allowedRoles = options.allowedRoles;

  const { data, error } = await supabase.auth.signInWithPassword({
    email: email.trim(),
    password,
  });

  if (error) {
    const hint = await explainLoginFailure(email);
    throw new Error(hint);
  }

  const account = await fetchUserAccount(data.user.id, data.user.email);
  if (!account) {
    await supabase.auth.signOut();
    clearSessionContext();
    throw new Error('No account found for this email. Gym admins can register at /register. Receptionists must be added by a gym admin.');
  }

  if (Array.isArray(allowedRoles) && allowedRoles.length > 0 && !allowedRoles.includes(account.role)) {
    await supabase.auth.signOut();
    clearSessionContext();
    if (allowedRoles.includes('owner') && !allowedRoles.includes('admin')) {
      throw new Error('This portal is for platform owners only. Gym admins and receptionists sign in at the main page (/).');
    }
    throw new Error('Owner accounts sign in at /owner — this page is for gym admins and receptionists.');
  }

  if ((account.role === 'admin' || account.role === 'receptionist') && !account.gym_id) {
    await supabase.auth.signOut();
    clearSessionContext();
    throw new Error(
      'This account is not linked to a gym. Ask the platform owner to fix your admin/receptionist gym link in Supabase (user_accounts.gym_id).',
    );
  }

  await assertAdminCanLogin(account);

  applySessionContext(account);
  return { user: data.user, session: data.session, account };
}

export async function signOut() {
  clearSessionContext();
  await clearCachedAccount();
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function registerGymAdmin({
  gymName,
  fullName,
  email,
  password,
  planId,
  billingCycle,
  useFreeTrial,
}) {
  const configError = getSupabaseConfigError();
  if (configError) throw new Error(configError);

  const trimmedEmail = email.trim().toLowerCase();
  const trimmedGym = gymName.trim();
  const trimmedName = fullName.trim();
  const cycle = billingCycleFromDuration(billingCycle);
  const availablePlans = await fetchSaasPlans();
  const selectedPlan = availablePlans.find((p) => p.id === planId);
  const planName = selectedPlan?.name || 'Starter';
  const price = getPriceForPlan(planId, cycle, availablePlans);
  const trialDays = useFreeTrial ? (selectedPlan?.trialDays || TRIAL_DAYS) : 0;

  // Already fully registered? Check user_accounts AND admins (orphan rows must not block signup).
  const { data: existingAccount } = await supabase
    .from('user_accounts')
    .select('id, role, gym_id, status, auth_user_id, email')
    .ilike('email', trimmedEmail)
    .maybeSingle();

  if (existingAccount?.gym_id && existingAccount.role === 'admin') {
    let adminExists = false;

    if (existingAccount.auth_user_id) {
      const { data: byAuth } = await supabase
        .from('admins')
        .select('id')
        .eq('auth_user_id', existingAccount.auth_user_id)
        .maybeSingle();
      adminExists = Boolean(byAuth?.id);
    }

    if (!adminExists) {
      const { data: byEmail } = await supabase
        .from('admins')
        .select('id')
        .ilike('email', trimmedEmail)
        .maybeSingle();
      adminExists = Boolean(byEmail?.id);
    }

    if (adminExists) {
      throw new Error('This email already has a gym admin account. Please sign in instead.');
    }

    // Orphan user_accounts row (no matching admins) — allow signup to repair / continue
  }

  const { user, session } = await createAdminAuthUser(trimmedEmail, password, trimmedName);

  const account = await provisionGymAdmin({
    authUserId: user.id,
    gymName: trimmedGym,
    fullName: trimmedName,
    email: trimmedEmail,
    planId,
    planName,
    cycle,
    price,
    trialDays,
    useFreeTrial,
    existingAccountId: existingAccount?.id || null,
  });

  return {
    user,
    session,
    account,
    needsEmailConfirmation: !session,
    trialDays: trialDays || null,
  };
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isAuthRateLimited(error) {
  if (!error) return false;
  const message = String(error.message || error.error_description || '');
  return (
    error.status === 429
    || error.code === 'over_email_send_rate_limit'
    || /rate.?limit|too many requests|email.*rate|over_email/i.test(message)
  );
}

function isAlreadyRegistered(error) {
  if (!error) return false;
  return /already registered|already exists|user already|email.*exist/i.test(
    String(error.message || ''),
  );
}

function getAuthEmailRedirectTo() {
  if (typeof window === 'undefined') return undefined;
  // After confirm, land on login so the new admin can sign in
  const origin = window.location.origin.replace(/\/$/, '');
  return `${origin}/`;
}

export async function resendAdminConfirmationEmail(email) {
  const trimmed = String(email || '').trim().toLowerCase();
  if (!trimmed) throw new Error('Email is required.');

  const { error } = await supabase.auth.resend({
    type: 'signup',
    email: trimmed,
    options: {
      emailRedirectTo: getAuthEmailRedirectTo(),
    },
  });

  if (error) {
    if (isAuthRateLimited(error)) {
      throw new Error('Email was just sent. Wait about 60 seconds, then try Resend again.');
    }
    throw new Error(formatSupabaseError(error, 'Could not resend confirmation email.'));
  }
}

/**
 * Create Auth user with retries when Supabase email rate-limit (429) hits.
 * If the email was created on a previous attempt, sign in and continue.
 */
async function createAdminAuthUser(email, password, fullName) {
  const maxAttempts = 4;
  let lastError = null;
  const emailRedirectTo = getAuthEmailRedirectTo();
  const normalizedEmail = String(email || '').trim().toLowerCase();

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: {
        data: { full_name: fullName, role: 'admin' },
        emailRedirectTo,
      },
    });

    if (!authError && authData?.user?.id) {
      const identities = authData.user.identities || [];
      // Supabase returns a user with empty identities when the email already exists
      if (!authData.session && identities.length === 0) {
        await supabase.auth.resend({
          type: 'signup',
          email: normalizedEmail,
          options: { emailRedirectTo },
        }).catch(() => null);
        throw new Error(
          'This email is already registered. If you have not confirmed it yet, check inbox/spam for the confirmation link (we tried to resend it). Otherwise sign in instead.',
        );
      }

      // signUp already triggers the confirmation email once.
      // Do NOT resend immediately — Gmail SMTP often rate-limits / spam-filters the double send,
      // which looks like “wrong email bounces but correct email never arrives”.
      return { user: authData.user, session: authData.session || null };
    }

    lastError = authError;

    if (isAlreadyRegistered(authError)) {
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password,
      });
      if (signInError) {
        // Unconfirmed users cannot sign in — resend the link
        if (/confirm|verify|email not confirmed/i.test(signInError.message || '')) {
          await supabase.auth.resend({
            type: 'signup',
            email: normalizedEmail,
            options: { emailRedirectTo },
          }).catch(() => null);
          throw new Error(
            'This email is registered but not confirmed yet. We sent a new confirmation link — check inbox and spam.',
          );
        }
        throw new Error(
          'This email is already registered. Sign in with the same password, or reset it in Supabase → Authentication → Users.',
        );
      }
      return { user: signInData.user, session: signInData.session };
    }

    if (isAuthRateLimited(authError) && attempt < maxAttempts) {
      await sleep(2500 * attempt);
      continue;
    }

    if (isAuthRateLimited(authError)) {
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password,
      });
      if (!signInError && signInData?.user) {
        return { user: signInData.user, session: signInData.session };
      }
      throw new Error(
        'Signup is temporarily busy (email limit). Wait about 1 minute, then submit again with the same details — registration will resume automatically.',
      );
    }

    throw new Error(formatSupabaseError(authError, 'Could not create login account.'));
  }

  throw new Error(formatSupabaseError(lastError, 'Could not create login account.'));
}

async function provisionGymAdmin({
  authUserId,
  gymName,
  fullName,
  email,
  planId,
  planName,
  cycle,
  price,
  trialDays,
  useFreeTrial,
  existingAccountId,
}) {
  // Resume: admin account already linked to a gym
  const { data: existingAdmin } = await supabase
    .from('admins')
    .select('id, gym_id, user_account_id')
    .eq('auth_user_id', authUserId)
    .maybeSingle();

  if (existingAdmin?.gym_id) {
    const { data: accountRow } = await supabase
      .from('user_accounts')
      .select('*')
      .eq('auth_user_id', authUserId)
      .maybeSingle();

    ensureGymAdminSenderEmail(email, '', existingAdmin.gym_id);
    setActiveGymId(existingAdmin.gym_id);
    applySessionContext(accountRow || {
      auth_user_id: authUserId,
      role: 'admin',
      gym_id: existingAdmin.gym_id,
      full_name: fullName,
      email,
      status: 'active',
    });

    return accountRow || {
      auth_user_id: authUserId,
      role: 'admin',
      gym_id: existingAdmin.gym_id,
      full_name: fullName,
      email,
      status: 'active',
    };
  }

  const { data: gym, error: gymError } = await supabase
    .from('gyms')
    .insert(stampCreatedAt({
      name: gymName,
      contact_email: email,
      status: 'active',
    }))
    .select('id')
    .single();

  if (gymError) {
    if (/gyms_name_uq|duplicate key|unique constraint/i.test(gymError.message || '')) {
      throw new Error(
        `A gym named "${gymName}" already exists. Use a different gym name, or sign in if this gym is already yours.`,
      );
    }
    throw new Error(formatSupabaseError(gymError, 'Could not create gym.'));
  }

  let userAccountId = existingAccountId;

  if (userAccountId) {
    const { error: linkError } = await supabase
      .from('user_accounts')
      .update({
        auth_user_id: authUserId,
        role: 'admin',
        gym_id: gym.id,
        full_name: fullName,
        email,
        status: 'active',
      })
      .eq('id', userAccountId);
    if (linkError) {
      throw new Error(formatSupabaseError(linkError, 'Could not link admin account.'));
    }
  } else {
    const { data: userAccount, error: accountError } = await supabase
      .from('user_accounts')
      .insert(stampCreatedAt({
        auth_user_id: authUserId,
        role: 'admin',
        gym_id: gym.id,
        full_name: fullName,
        email,
        status: 'active',
      }))
      .select('id')
      .single();

    if (accountError) {
      // Race: account row may already exist for this auth user
      const { data: byAuth } = await supabase
        .from('user_accounts')
        .select('id')
        .eq('auth_user_id', authUserId)
        .maybeSingle();
      if (byAuth?.id) {
        userAccountId = byAuth.id;
        await supabase
          .from('user_accounts')
          .update({ gym_id: gym.id, role: 'admin', full_name: fullName, email, status: 'active' })
          .eq('id', byAuth.id);
      } else {
        throw new Error(formatSupabaseError(accountError, 'Could not create admin account.'));
      }
    } else {
      userAccountId = userAccount.id;
    }
  }

  const { error: adminError } = await supabase.from('admins').insert(stampCreatedAt({
    auth_user_id: authUserId,
    gym_id: gym.id,
    user_account_id: userAccountId,
    full_name: fullName,
    email,
    source: 'signup',
    status: 'active',
  }));

  if (adminError && !/duplicate|unique/i.test(adminError.message || '')) {
    throw new Error(formatSupabaseError(adminError, 'Could not create admin profile.'));
  }

  const startDate = new Date().toISOString().slice(0, 10);
  const trialEndsAt = trialDays
    ? new Date(Date.now() + trialDays * 24 * 60 * 60 * 1000).toISOString()
    : null;
  const subscriptionStatus = useFreeTrial ? 'trial' : 'active';

  const { data: existingSub } = await supabase
    .from('gym_subscriptions')
    .select('id')
    .eq('gym_id', gym.id)
    .limit(1)
    .maybeSingle();

  if (!existingSub) {
    const { error: subError } = await supabase.from('gym_subscriptions').insert(stampCreatedAt({
      gym_id: gym.id,
      plan_id: planId,
      plan_name: planName,
      billing_cycle: cycle,
      price,
      start_date: startDate,
      expiry_date: useFreeTrial
        ? new Date(Date.now() + trialDays * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
        : computeExpiryDate(startDate, cycle),
      trial_ends_at: trialEndsAt,
      status: subscriptionStatus,
      source: 'signup',
      contact_email: email,
    }));

    if (subError) {
      throw new Error(formatSupabaseError(subError, 'Could not create subscription.'));
    }
  }

  // First admin for this gym → sender From-email is their signup address
  ensureGymAdminSenderEmail(email, gymName, gym.id);
  setActiveGymId(gym.id);

  // Permanent walk-in member (serial 1) for this gym
  try {
    const { ensureWalkInMemberForGym } = await import('../receptionist/walkInMember');
    await ensureWalkInMemberForGym(gym.id);
  } catch (walkInError) {
    console.warn('Could not create walk-in member for new gym', walkInError?.message || walkInError);
  }

  const account = {
    auth_user_id: authUserId,
    role: 'admin',
    gym_id: gym.id,
    full_name: fullName,
    email,
    status: 'active',
  };

  applySessionContext(account);
  return account;
}

export function getDashboardPath(role) {
  return ROLE_ROUTES[role] || '/';
}

/** Create Supabase Auth login for a receptionist (admin adds from panel) */
export async function createReceptionistAuth({
  email,
  password,
  fullName,
  gymId,
  receptionistId,
}) {
  const configError = getSupabaseConfigError();
  if (configError) throw new Error(configError);

  const trimmedEmail = email?.trim();
  if (!trimmedEmail) throw new Error('Email is required for receptionist login.');
  if (!password || password.length < 6) {
    throw new Error('Password must be at least 6 characters.');
  }

  const { data: existingRows, error: existingLookupError } = await supabase
    .from('user_accounts')
    .select('id, staff_id, auth_user_id, email')
    .ilike('email', trimmedEmail)
    .limit(10);

  if (existingLookupError) {
    throw new Error(formatSupabaseError(existingLookupError, 'Could not check existing accounts.'));
  }

  const existingAccount = (existingRows || []).find(
    (row) => String(row.email || '').trim().toLowerCase() === trimmedEmail.toLowerCase(),
  ) || existingRows?.[0] || null;

  if (existingAccount?.auth_user_id && existingAccount.staff_id === receptionistId) {
    return existingAccount.auth_user_id;
  }

  if (existingAccount) {
    throw new Error('This email already has a login account for another user. Use a different email.');
  }

  const { data: { session: adminSession } } = await supabase.auth.getSession();

  const { data: authData, error: authError } = await supabase.auth.signUp({
    email: trimmedEmail,
    password,
    options: {
      data: { full_name: fullName?.trim() || '', role: 'receptionist' },
    },
  });

  let authUserId = authData.user?.id || null;

  if (authError) {
    if (authError.status === 429 || /too many requests/i.test(authError.message)) {
      throw new Error(
        'Too many signup attempts. Wait 1–2 minutes, then try again. Or add the user in Supabase → Authentication → Users (check Auto Confirm), then set password here and save.',
      );
    }
    if (/already registered|already exists/i.test(authError.message)) {
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email: trimmedEmail,
        password,
      });

      if (signInError) {
        throw new Error(
          'This email already exists in Supabase Auth with a different password. Reset it in Supabase → Authentication → Users, then save again here with the new password.',
        );
      }

      authUserId = signInData.user?.id || null;
      if (adminSession) {
        await supabase.auth.setSession({
          access_token: adminSession.access_token,
          refresh_token: adminSession.refresh_token,
        });
      }
    } else {
      throw authError;
    }
  } else if (adminSession) {
    await supabase.auth.setSession({
      access_token: adminSession.access_token,
      refresh_token: adminSession.refresh_token,
    });
  }

  if (!authUserId) {
    throw new Error('Could not create receptionist login.');
  }

  const { error: receptionistError } = await supabase
    .from('receptionists')
    .update({
      auth_user_id: authUserId,
      has_login: true,
      login_email: trimmedEmail,
    })
    .eq('id', receptionistId);

  if (receptionistError) {
    throw new Error(formatSupabaseError(receptionistError, 'Could not link receptionist login.'));
  }

  const { error: accountError } = await supabase.from('user_accounts').insert(stampCreatedAt({
    auth_user_id: authUserId,
    role: 'receptionist',
    gym_id: gymId,
    staff_id: receptionistId,
    full_name: fullName?.trim() || '',
    email: trimmedEmail,
    status: 'active',
  }));

  if (accountError) {
    throw new Error(formatSupabaseError(accountError, 'Could not create receptionist account.'));
  }

  return authUserId;
}

/**
 * Create Auth + user_accounts (role=owner) for an additional owner-panel staff user.
 * Restores the current owner session after signUp so the creator stays logged in.
 */
export async function createOwnerPanelUserAuth({ email, password, fullName }) {
  const configError = getSupabaseConfigError();
  if (configError) throw new Error(configError);

  const trimmedEmail = email?.trim();
  if (!trimmedEmail) throw new Error('Email is required for owner panel login.');
  if (!password || password.length < 6) {
    throw new Error('Password must be at least 6 characters.');
  }

  const { data: existingAccount } = await supabase
    .from('user_accounts')
    .select('id, auth_user_id, role')
    .ilike('email', trimmedEmail)
    .maybeSingle();

  if (existingAccount) {
    throw new Error('This email already has a login account. Use a different email.');
  }

  const { data: { session: ownerSession } } = await supabase.auth.getSession();

  const { data: authData, error: authError } = await supabase.auth.signUp({
    email: trimmedEmail,
    password,
    options: {
      data: { full_name: fullName?.trim() || '', role: 'owner' },
    },
  });

  let authUserId = authData.user?.id || null;

  if (authError) {
    if (authError.status === 429 || /too many requests/i.test(authError.message)) {
      throw new Error(
        'Too many signup attempts. Wait 1–2 minutes, then try again. Or add the user in Supabase → Authentication → Users (Auto Confirm on), then add them here with the same email/password.',
      );
    }
    if (/already registered|already exists/i.test(authError.message)) {
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email: trimmedEmail,
        password,
      });

      if (signInError) {
        throw new Error(
          'This email already exists in Supabase Auth with a different password. Reset it in Supabase → Authentication → Users, then try again.',
        );
      }

      authUserId = signInData.user?.id || null;
      if (ownerSession) {
        await supabase.auth.setSession({
          access_token: ownerSession.access_token,
          refresh_token: ownerSession.refresh_token,
        });
      }
    } else {
      throw authError;
    }
  } else if (ownerSession) {
    await supabase.auth.setSession({
      access_token: ownerSession.access_token,
      refresh_token: ownerSession.refresh_token,
    });
  }

  if (!authUserId) {
    throw new Error('Could not create owner panel login.');
  }

  const { error: accountError } = await supabase.from('user_accounts').insert(stampCreatedAt({
    auth_user_id: authUserId,
    role: 'owner',
    full_name: fullName?.trim() || '',
    email: trimmedEmail,
    status: 'active',
  }));

  if (accountError) {
    throw new Error(formatSupabaseError(accountError, 'Could not create owner panel account.'));
  }

  return authUserId;
}

/**
 * Create Auth + user_accounts (role=admin) + admins row for a gym-scoped admin panel user.
 * They can only access the gym they were added to. Restores the creator's session after signUp.
 */
export async function createAdminPanelUserAuth({ email, password, fullName, gymId }) {
  const configError = getSupabaseConfigError();
  if (configError) throw new Error(configError);

  const trimmedEmail = email?.trim();
  if (!trimmedEmail) throw new Error('Email is required for admin panel login.');
  if (!password || password.length < 6) {
    throw new Error('Password must be at least 6 characters.');
  }
  if (!gymId) throw new Error('Gym context is missing. Sign in again.');

  const { data: existingAccount } = await supabase
    .from('user_accounts')
    .select('id, auth_user_id, role, gym_id')
    .ilike('email', trimmedEmail)
    .maybeSingle();

  if (existingAccount) {
    throw new Error('This email already has a login account. Use a different email.');
  }

  const { data: { session: adminSession } } = await supabase.auth.getSession();

  const { data: authData, error: authError } = await supabase.auth.signUp({
    email: trimmedEmail,
    password,
    options: {
      data: { full_name: fullName?.trim() || '', role: 'admin', gym_id: gymId },
    },
  });

  let authUserId = authData.user?.id || null;

  if (authError) {
    if (authError.status === 429 || /too many requests/i.test(authError.message)) {
      throw new Error(
        'Too many signup attempts. Wait 1–2 minutes, then try again. Or add the user in Supabase → Authentication → Users (Auto Confirm on), then add them here with the same email/password.',
      );
    }
    if (/already registered|already exists/i.test(authError.message)) {
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email: trimmedEmail,
        password,
      });

      if (signInError) {
        throw new Error(
          'This email already exists in Supabase Auth with a different password. Reset it in Supabase → Authentication → Users, then try again.',
        );
      }

      authUserId = signInData.user?.id || null;
      if (adminSession) {
        await supabase.auth.setSession({
          access_token: adminSession.access_token,
          refresh_token: adminSession.refresh_token,
        });
      }
    } else {
      throw authError;
    }
  } else if (adminSession) {
    await supabase.auth.setSession({
      access_token: adminSession.access_token,
      refresh_token: adminSession.refresh_token,
    });
  }

  if (!authUserId) {
    throw new Error('Could not create admin panel login.');
  }

  const { data: userAccount, error: accountError } = await supabase
    .from('user_accounts')
    .insert(stampCreatedAt({
      auth_user_id: authUserId,
      role: 'admin',
      gym_id: gymId,
      full_name: fullName?.trim() || '',
      email: trimmedEmail,
      status: 'active',
    }))
    .select('id')
    .single();

  if (accountError) {
    throw new Error(formatSupabaseError(accountError, 'Could not create admin panel account.'));
  }

  const { error: adminError } = await supabase.from('admins').insert(stampCreatedAt({
    auth_user_id: authUserId,
    gym_id: gymId,
    user_account_id: userAccount?.id || null,
    full_name: fullName?.trim() || '',
    email: trimmedEmail,
    source: 'panel_user',
    status: 'active',
  }));

  if (adminError) {
    await supabase.from('user_accounts').delete().eq('auth_user_id', authUserId);
    throw new Error(formatSupabaseError(adminError, 'Could not create admin profile for this gym.'));
  }

  return authUserId;
}

/** Signed-in user changes their own Auth password after confirming the current one. */
export async function changeOwnPassword({ email, currentPassword, newPassword }) {
  const trimmedEmail = String(email || '').trim();
  const current = String(currentPassword || '');
  const next = String(newPassword || '');

  if (!trimmedEmail) {
    throw new Error('Your login email is missing. Sign out and sign in again.');
  }
  if (!current) {
    throw new Error('Enter your current password.');
  }
  if (next.length < 6) {
    throw new Error('New password must be at least 6 characters.');
  }
  if (current === next) {
    throw new Error('New password must be different from the current password.');
  }

  const { error: verifyError } = await supabase.auth.signInWithPassword({
    email: trimmedEmail,
    password: current,
  });
  if (verifyError) {
    throw new Error('Current password is incorrect.');
  }

  const { error } = await supabase.auth.updateUser({ password: next });
  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not update password.'));
  }
}

const PASSWORD_RECOVERY_KEY = 'gymcrm_password_recovery';

export function markPasswordRecovery() {
  try {
    sessionStorage.setItem(PASSWORD_RECOVERY_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function clearPasswordRecovery() {
  try {
    sessionStorage.removeItem(PASSWORD_RECOVERY_KEY);
  } catch {
    /* ignore */
  }
}

export function isPasswordRecoveryPending() {
  if (typeof window === 'undefined') return false;
  try {
    if (sessionStorage.getItem(PASSWORD_RECOVERY_KEY) === '1') return true;
  } catch {
    /* ignore */
  }
  const blob = `${window.location.hash || ''}${window.location.search || ''}`;
  return /type=recovery/i.test(blob);
}

export async function requestStaffPasswordReset(email) {
  const configError = getSupabaseConfigError();
  if (configError) throw new Error(configError);

  const trimmed = String(email || '').trim();
  if (!trimmed) {
    throw new Error('Enter your login email.');
  }

  const { error } = await supabase.auth.resetPasswordForEmail(trimmed, {
    redirectTo: `${window.location.origin}/`,
  });
  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not send the reset email.'));
  }
}

export async function completePasswordReset({ newPassword, confirmPassword }) {
  const next = String(newPassword || '');
  const confirm = String(confirmPassword || '');
  if (next.length < 6) {
    throw new Error('New password must be at least 6 characters.');
  }
  if (next !== confirm) {
    throw new Error('New password and confirmation do not match.');
  }

  const { error } = await supabase.auth.updateUser({ password: next });
  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not update password.'));
  }
  clearPasswordRecovery();
}

/** Admin/owner sets a receptionist's Auth password without knowing the current one. */
export async function updateReceptionistAuthPassword({ receptionistId, newPassword }) {
  const next = String(newPassword || '');
  if (!receptionistId) {
    throw new Error('Receptionist id is required.');
  }
  if (next.length < 6) {
    throw new Error('New password must be at least 6 characters.');
  }

  const { data, error } = await supabase.rpc('admin_set_receptionist_password', {
    p_receptionist_id: receptionistId,
    p_new_password: next,
  });

  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not update receptionist password.'));
  }
  return data === true || data == null;
}
